/**
 * Shared expert-review UI: RadioOptionRow.tsx.
 *
 * Belongs here: reusable presentational piece.
 * Does not belong here: flow rules or API.
 */
import { useEffect, useRef, useState } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import RenderHTML from '../../../../../components/ui-components/RenderHTML'
import { PrimaryButton } from '../../../../../components/execution/PrimaryButton'
import { CONFIRM_ACTION_DELAY_MS } from '../transitions/contentTransitionTimings'
import type { QuestionOptionReturnMetadata } from '../types/questionnaire.types'

const SELECTION_FEEDBACK_DURATION_MS = 550

export interface RadioOptionRowOption {
  id?: string
  displayText: string
  subText?: string
  expertRecommended?: boolean
  metadata?: QuestionOptionReturnMetadata | null
}

export const UnCheckedIcon = `<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32" fill="none">
  <path d="M16 7.75C20.5564 7.75 24.25 11.4436 24.25 16C24.25 20.5564 20.5564 24.25 16 24.25C11.4436 24.25 7.75 20.5564 7.75 16C7.75 11.4437 11.4437 7.75 16 7.75Z" stroke="#D1D6D6" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
</svg>`

export const CheckedIcon = `<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32" fill="none">
  <path d="M16 7.75C20.5564 7.75 24.25 11.4436 24.25 16C24.25 20.5564 20.5564 24.25 16 24.25C11.4436 24.25 7.75 20.5564 7.75 16C7.75 11.4437 11.4437 7.75 16 7.75Z" stroke="#D1D6D6" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
  <path d="M16 25C20.9706 25 25 20.9706 25 16C25 11.0294 20.9706 7 16 7C11.0294 7 7 11.0294 7 16C7 20.9706 11.0294 25 16 25Z" fill="#232323"/>
  <path d="M12.5 17L14.5833 19L19.25 13" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
</svg>`

interface RadioOptionRowProps {
  option: RadioOptionRowOption
  selected: boolean
  /** Return `false` to ignore the tap (no feedback / no side effects). */
  onSelect: () => boolean | void
  showSeparator?: boolean
  requiresConfirm?: boolean
  onConfirm?: () => void
  /** Fires when the select shimmer/scale animation finishes (auto-advance path). */
  onSelectionAnimationEnd?: () => void
  testLocator?: string
}

export function RadioOptionRow({
  option,
  selected,
  onSelect,
  showSeparator = true,
  requiresConfirm = false,
  onConfirm,
  onSelectionAnimationEnd,
  testLocator
}: RadioOptionRowProps) {
  const [isShowingFeedback, setIsShowingFeedback] = useState(false)
  const feedbackTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const [isConfirming, setIsConfirming] = useState(false)
  const hasReturnMetadata =
    option.metadata?.bestCaseReturns != null ||
    option.metadata?.averageReturns != null ||
    option.metadata?.worstCaseReturns != null

  useEffect(() => {
    return () => {
      if (feedbackTimeoutRef.current) clearTimeout(feedbackTimeoutRef.current)
    }
  }, [])

  const handleClick = () => {
    if (isShowingFeedback || isConfirming) return
    if (onSelect() === false) return
    if (!requiresConfirm && onSelectionAnimationEnd) {
      setIsShowingFeedback(true)
      if (feedbackTimeoutRef.current) clearTimeout(feedbackTimeoutRef.current)
      feedbackTimeoutRef.current = setTimeout(() => {
        feedbackTimeoutRef.current = null
        setIsShowingFeedback(false)
        onSelectionAnimationEnd()
      }, SELECTION_FEEDBACK_DURATION_MS)
    }
  }

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={handleClick}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault()
          handleClick()
        }
      }}
      className={`flex w-full flex-col cursor-pointer text-left bg-white ${showSeparator ? 'border-b border-gray-200' : ''}`}
      aria-pressed={selected}
    >
      <div
        className={`flex w-full ${option.subText ? 'items-center' : 'items-center'} gap-4 p-4 pt-[14px] ${selected && (hasReturnMetadata || requiresConfirm) ? 'pb-2' : ''
          }`}
      >
        <span className="mt-[1px] shrink-0 text-gray-400" aria-hidden>
          <RenderHTML innerHTMLText={selected ? CheckedIcon : UnCheckedIcon} className="[&>svg]:block" />
        </span>
        <div
          className={`min-w-0 flex-1 relative overflow-hidden ${isShowingFeedback ? 'radio-option-feedback-scale' : ''}`}
        >
          <div className="flex flex-wrap items-center gap-2">
            <p
              className="font-[500] text-[14px] text-[#232323]"
              {...(testLocator ? { 'test-locator': testLocator } : {})}
            >
              {option.displayText}
            </p>
          </div>
          {option.subText ? (
            <p className="mt-0.5 text-[13px] text-[#6A6E6E] leading-[130%]">{option.subText}</p>
          ) : null}
          {option.expertRecommended ? <ExpertRecommendedTag /> : null}
          {hasReturnMetadata && option.metadata ? (
            <OptionReturnMetrics
              isExpanded={selected}
              metadata={option.metadata}
              testLocator={testLocator}
            />
          ) : null}
          {isShowingFeedback ? <span className="radio-option-shimmer" aria-hidden /> : null}
        </div>
      </div>
      {requiresConfirm && selected && onConfirm ? (
        <div className="px-4 pb-4 pt-2 pl-16" onClick={(event) => event.stopPropagation()}>
          <PrimaryButton
            testLocator={testLocator ? `${testLocator}-confirm` : undefined}
            onClick={() => {
              if (isConfirming) return
              setIsConfirming(true)
              setTimeout(() => {
                onConfirm()
              }, CONFIRM_ACTION_DELAY_MS)
            }}
            label="Confirm"
          >
            {isConfirming ? (
              <span className="flex items-center justify-center gap-2">
                <RenderHTML innerHTMLText={CONFIRMED_ICON_SVG} />
              </span>
            ) : null}
          </PrimaryButton>
        </div>
      ) : null}
      <style
        dangerouslySetInnerHTML={{
          __html: `
            @keyframes radio-option-shimmer-keyframes {
              0% { transform: translateX(-100%); }
              40% { transform: translateX(80%); }
              100% { transform: translateX(250%); }
            }
            .radio-option-shimmer {
              position: absolute;
              top: 0;
              right: 0;
              bottom: 0;
              left: 0;
              width: 55%;
              background: linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.5) 30%, rgba(255,255,255,0.85) 50%, rgba(255,255,255,0.5) 70%, transparent 100%);
              animation: radio-option-shimmer-keyframes 0.55s ease-out forwards;
              pointer-events: none;
            }
            @keyframes radio-option-feedback-scale-keyframes {
              0% { transform: scale(1); }
              35% { transform: scale(1.06); }
              70% { transform: scale(1.06); }
              100% { transform: scale(1); }
            }
            .radio-option-feedback-scale {
              transform-origin: left center;
              animation: radio-option-feedback-scale-keyframes 0.55s ease-out forwards;
            }
            @keyframes radio-option-feedback-color-display-keyframes {
              0%, 40% { color: #232323; }
              48% { color: #095EB2; }
              52% { color: #095EB2; }
              60%, 100% { color: #232323; }
            }
            @keyframes radio-option-feedback-color-subtext-keyframes {
              0%, 40% { color: #6A6E6E; }
              48% { color: #095EB2; }
              52% { color: #095EB2; }
              60%, 100% { color: #6A6E6E; }
            }
            .radio-option-feedback-scale > div:first-child > p {
              animation: radio-option-feedback-color-display-keyframes 0.55s ease-out forwards;
            }
            .radio-option-feedback-scale > p {
              animation: radio-option-feedback-color-subtext-keyframes 0.55s ease-out forwards;
            }
          `
        }}
      />
    </div>
  )
}

export const CONFIRMED_ICON_SVG = `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
  <path d="M2.5 9.66667L5.89506 13L13.5 3" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
</svg>`

export function ExpertRecommendedTag() {
  return (
    <div
      className="mt-[13px] mb-1 flex w-max items-center justify-center rounded-[8px] px-2 pt-[2px] pb-[3px]"
      style={{ backgroundImage: 'linear-gradient(102deg, var(--Prime-Prime10, #FCF1ED) 58.81%, var(--Prime-Prime20, #EDCEC4) 117.29%)' }}
    >
      <p className="whitespace-nowrap text-[12px] font-[500] leading-[140%] text-[#732E17]">Recommended</p>
    </div>
  )
}

/** Formats option return % for badges — e.g. + 15% / - 3%. */
export function formatOptionReturnPercent(value: number): string {
  if (value > 0) return `+ ${value}%`
  if (value < 0) return `- ${Math.abs(value)}%`
  return `${value}%`
}

export function OptionReturnMetrics({
  metadata,
  testLocator,
  isExpanded
}: {
  metadata: QuestionOptionReturnMetadata
  testLocator?: string
  isExpanded: boolean
}) {
  const shouldAnimateEnterRef = useRef(!isExpanded)

  useEffect(() => {
    if (!isExpanded) {
      shouldAnimateEnterRef.current = true
    }
  }, [isExpanded])

  const rows: {
    key: string
    value: number
    label: string
    badgeClassName: string
    labelClassName: string
  }[] = []
  if (metadata.bestCaseReturns != null) {
    rows.push({
      key: 'best',
      value: metadata.bestCaseReturns,
      label: 'Best case returns',
      badgeClassName: 'bg-[#E4FCEB] text-[#00661D]',
      labelClassName: 'text-[#00661D]'
    })
  }
  if (metadata.averageReturns != null) {
    rows.push({
      key: 'average',
      value: metadata.averageReturns,
      label: 'Average returns',
      badgeClassName: 'bg-[#F0F4F7] text-[#000000]',
      labelClassName: 'text-[#000000]'
    })
  }
  if (metadata.worstCaseReturns != null) {
    rows.push({
      key: 'worst',
      value: metadata.worstCaseReturns,
      label: 'Worst case returns',
      badgeClassName: 'bg-[#FFE9E6] text-[#992617]',
      labelClassName: 'text-[#992617]'
    })
  }

  if (rows.length === 0) return null

  return (
    <AnimatePresence>
      {isExpanded ? (
        <motion.div
          initial={shouldAnimateEnterRef.current ? { height: 0 } : false}
          animate={{ height: 'auto' }}
          exit={{ height: 0 }}
          transition={{ type: 'spring', duration: 0.4, bounce: 0 }}
          className="overflow-hidden"
        >
          <div
            className="mt-[14px] mb-1 flex flex-col gap-[3px]"
            {...(testLocator ? { 'test-locator': `${testLocator}-return-metrics` } : {})}
          >
            {rows.map((row) => (
              <div key={row.key} className="flex items-center gap-[6px]">
                <span
                  className={`inline-flex min-w-[42px] shrink-0 items-center justify-center rounded-[4px] px-[3px] py-[1px] text-[12px] font-[700] leading-[140%] ${row.badgeClassName}`}
                >
                  {formatOptionReturnPercent(row.value)}
                </span>
                <span className={`text-[13px] font-[500] leading-[140%] ${row.labelClassName}`}>{row.label}</span>
              </div>
            ))}
          </div>
        </motion.div>
      ) : null}
    </AnimatePresence>
  )
}
